import { Component, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, Observable, switchMap, take, takeUntil } from 'rxjs';
import { CreateCaseDialogComponent } from '../../account/manage-case/create-case-dialog/create-case-dialog.component';
import { BaseComponent } from '../../base.component';
import { UserConfigsService } from '../../../services/user-configs.service';
import { LexbeCase } from '../../../models/case.model';
import { Role } from '../../../models/role.model';
import { Account } from '../../../models/ui/account.model';
import { AccountManager } from '../../../managers/account.manager';
import { CaseAccessManager } from '../../../managers/case-access.manager';
import { NotificationManager } from '../../../managers/notification.manager';
import { CreateAccountDialogComponent } from './create-account-dialog/create-account-dialog.component';
import { CaseAdminSdk } from '../../../sdks/case-admin.sdk';

@Component({
    selector: 'lex-accounts',
    templateUrl: './accounts.component.html',
    styleUrls: ['./accounts.component.scss'],
    standalone: false
})
export class AccountsComponent extends BaseComponent {

	@ViewChild('leftScroll') leftScroll: CdkVirtualScrollViewport;

	public accounts$: Observable<Account[]>;
	public selectedAccount: Account = null;
	private selectedAccountId: string = '';

	private searchTerm = new BehaviorSubject<string>('');
	public searchText: string = '';

	constructor(
		private accountManager: AccountManager,
		private caseAccessManager: CaseAccessManager,
		private notificationManager: NotificationManager,
		private userConfigsService: UserConfigsService,
		private caseAdminSdk: CaseAdminSdk,
		private dialog: MatDialog,
		private router: Router,
		private activatedRoute: ActivatedRoute) {
		super();
		this.accounts$ = this.accountManager.accountCollection$;
		this.accounts$.pipe(
			switchMap((data) => {
				if (data.length === 0 && this.selectedAccount) {
					this.selectAccount(null)
				}
				if (data.length === 1) {
					this.selectAccount(data[0]);
				}
				else if (this.selectedAccountId) {
					this.selectAccount(data.find(x => x.accountId === this.selectedAccountId));
					setTimeout(() => this.leftScroll.scrollToIndex(data.indexOf(this.selectedAccount)));
				}
				else if (this.selectedAccount?.accountId) {
					this.selectAccount(data.find(x => x.accountId === this.selectedAccount.accountId));
					setTimeout(() => this.leftScroll.scrollToIndex(data.indexOf(this.selectedAccount)));
				}
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
		this.searchTerm.pipe(
			debounceTime(500),
			distinctUntilChanged(),
			switchMap((term: string) => {
				this.accountManager.refreshAdminAccounts(term);
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
		this.activatedRoute.queryParams.pipe(
			switchMap((param) => {
				if (param['accountId']) {
					this.accounts$.pipe(take(1)).subscribe(data => {
						if (data.length > 0) {
							if (this.selectedAccount?.accountId !== param['accountId'])
								this.selectAccount(data.find(x => x.accountId === param['accountId']));
						}
						else
							this.selectedAccountId = param['accountId'];
					});
				}
				else if (!param['accountId'] && this.selectedAccount) {
					this.selectAccount(null);
				}
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	public selectAccount(account?: Account): void {
		if (account) {
			this.selectedAccount = account;
			this.selectedAccountId = '';
			this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: { accountId: this.selectedAccount.accountId } });
		}
		else {
			this.selectedAccount = null;
			this.router.navigate([], { relativeTo: this.activatedRoute });
		}
	}

	public addAccount(): void {
		this.addAccountDialog().pipe(
			switchMap(accountId => {
				if (accountId) {
					this.selectedAccountId = accountId;
					this.notificationManager.displaySnackBarMessage({
						message: 'Account created successfully.'
					});
					//Only GlobalAccountAdvancedFeatureSet and Administrator can manage user access,
					//so must check user roles before launching the dialog
					if (this.userConfigsService.inRoles(Role.GlobalAccountAdvancedFeatureSet, Role.Administrator, Role.ProfessionalServices))
						return this.createCase(accountId);
					return EMPTY;
				}
			}), catchError((e) => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to create account- ${e}`,
					type: 'error'
				});
				return EMPTY;
			}), finalize(() => {
				this.accountManager.refreshAdminAccounts(this.searchText);
			})
		).subscribe();
	}

	private addAccountDialog(): Observable<string> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		return this.dialog.open(CreateAccountDialogComponent, dialogConfig)
			.afterClosed()
			.pipe(
				switchMap((account) => {
					if (account)
						return this.accountManager.createAccount(account);
					return EMPTY;
				}));
	}

	private createCase(accountId: string): Observable<void> {
		return this.addCaseDialog(accountId)
			.pipe(switchMap((lexbeCase) => {				
				return this.caseAdminSdk.updateCaseUserAccess(lexbeCase.caseId, this.userConfigsService.currentConfig.userId)
					.pipe(switchMap(() => {
						this.notificationManager.displaySnackBarMessage({
							message: 'Case for account created successfully.'
						});
						return EMPTY;
					}), catchError((e) => {
						this.notificationManager.displaySnackBarMessage({
							message: `Failed to update case permission- ${e?.error?.error?.message}`,
							type: 'error'
						});
						return EMPTY;
					}));
			}), catchError((e) => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to create case- ${e?.error?.error?.message}`,
					type: 'error'
				});
				return EMPTY;
			}));
	}

	private addCaseDialog(accountId: string): Observable<LexbeCase> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = accountId;
		return this.dialog.open(CreateCaseDialogComponent, dialogConfig).afterClosed().pipe(switchMap((lexbeCase: LexbeCase) => {
			if (lexbeCase) {
				return this.caseAccessManager.createCase(lexbeCase);
			}
			return EMPTY;
		}));
	}

	public updateSearchText(term: string): void {
		this.searchText = term;
		this.searchTerm.next(term);
	}
}
